import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { eventEndpoints } from '../../api';
import { Button, EmptyRow, Panel, Table } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT, dollars, humanize } from './shared';

export function FeesPanel({ detail, refresh, onError, can }: PanelProps) {
  const editable = can('events.manage') && detail.event.status !== 'ARCHIVED';
  const feeRules = useQuery({ queryKey: ['fee-rules'], queryFn: eventEndpoints.feeRules });
  const taxRules = useQuery({ queryKey: ['tax-rules'], queryFn: eventEndpoints.taxRules });
  const [feeToAttach, setFeeToAttach] = useState('');
  const [taxToAttach, setTaxToAttach] = useState('');

  const attachFee = useMutation({ mutationFn: (id: string) => eventEndpoints.attachFeeRule(detail.event.id, id), onSuccess: () => { setFeeToAttach(''); refresh(); }, onError });
  const detachFee = useMutation({ mutationFn: (id: string) => eventEndpoints.detachFeeRule(detail.event.id, id), onSuccess: refresh, onError });
  const attachTax = useMutation({ mutationFn: (id: string) => eventEndpoints.attachTaxRule(detail.event.id, id), onSuccess: () => { setTaxToAttach(''); refresh(); }, onError });
  const detachTax = useMutation({ mutationFn: (id: string) => eventEndpoints.detachTaxRule(detail.event.id, id), onSuccess: refresh, onError });

  const attachedFees = new Set(detail.fee_rules.map((f) => f.id));
  const availableFees = (feeRules.data?.fee_rules ?? []).filter((f) => !attachedFees.has(f.id) && f.active === 1);
  const attachedTaxes = new Set(detail.tax_rules.map((t) => t.id));
  const availableTaxes = (taxRules.data?.tax_rules ?? []).filter((t) => !attachedTaxes.has(t.id) && t.active === 1);

  return (
    <div className="space-y-4">
      <Panel
        title="Fees on this event"
        description="Fee rules are shared configuration; attaching one here is what makes it apply. Each quote and sale uses the rule's active version."
        actions={
          editable ? (
            <div className="flex gap-2">
              <select value={feeToAttach} onChange={(e) => setFeeToAttach(e.target.value)} className={INPUT} aria-label="Fee rule to attach">
                <option value="">— attach a fee rule —</option>
                {availableFees.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name ?? f.code} ({humanize(f.fee_type)})
                  </option>
                ))}
              </select>
              <Button variant="primary" disabled={!feeToAttach || attachFee.isPending} onClick={() => attachFee.mutate(feeToAttach)}>
                Attach
              </Button>
            </div>
          ) : null
        }
      >
        <Table headers={['Fee', 'Type', 'Amount', 'Applies', 'Refundable', '']}>
          {detail.fee_rules.length === 0 ? (
            <EmptyRow columns={6}>
              No fees attached. Manage the shared rules under{' '}
              <Link to="/fees" className="text-accent underline-offset-2 hover:underline">
                Fees & taxes
              </Link>
              .
            </EmptyRow>
          ) : (
            detail.fee_rules.map((f) => {
              const full = feeRules.data?.fee_rules.find((r) => r.id === f.id);
              return (
                <tr key={f.id} className="border-b border-line last:border-0">
                  <td className="px-5 py-3">
                    <div className="font-medium text-ink-900">{f.name ?? f.code}</div>
                    <div className="text-xs text-ink-400">{f.code}{f.version_status !== 'ACTIVE' ? ' · no active version' : ''}{f.active === 0 ? ' · disabled' : ''}</div>
                  </td>
                  <td className="px-5 py-3 text-sm text-ink-600">{humanize(f.fee_type)}</td>
                  <td className="tabular px-5 py-3 text-sm text-ink-600">
                    {full?.calculation_method === 'PERCENT' ? `${(full.basis_points ?? 0) / 100}%` : full?.calculation_method === 'PRICE_BANDS' ? 'by price band' : dollars(full?.amount_cents)}
                    {full?.calculation_method === 'FIXED_PLUS_PERCENT' ? ` + ${(full.basis_points ?? 0) / 100}%` : ''}
                    {full?.included_in_price ? ' (in price)' : ''}
                  </td>
                  <td className="px-5 py-3 text-sm text-ink-600">{humanize(full?.application)}</td>
                  <td className="px-5 py-3 text-sm text-ink-600">{full?.refundable ? 'yes' : 'no'}</td>
                  <td className="px-5 py-3 text-right">{editable ? <Button variant="danger" onClick={() => detachFee.mutate(f.id)}>Detach</Button> : null}</td>
                </tr>
              );
            })
          )}
        </Table>
      </Panel>

      <Panel
        title="Taxes on this event"
        description="Which taxes apply is open question Q-03; attach whichever rules the venue's accountant confirms."
        actions={
          editable ? (
            <div className="flex gap-2">
              <select value={taxToAttach} onChange={(e) => setTaxToAttach(e.target.value)} className={INPUT} aria-label="Tax rule to attach">
                <option value="">— attach a tax rule —</option>
                {availableTaxes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name ?? t.code} ({(t.rate_ppm ?? 0) / 10000}%)
                  </option>
                ))}
              </select>
              <Button variant="primary" disabled={!taxToAttach || attachTax.isPending} onClick={() => attachTax.mutate(taxToAttach)}>
                Attach
              </Button>
            </div>
          ) : null
        }
      >
        <Table headers={['Tax', 'Jurisdiction', 'Rate', '']}>
          {detail.tax_rules.length === 0 ? (
            <EmptyRow columns={4}>No taxes attached.</EmptyRow>
          ) : (
            detail.tax_rules.map((t) => (
              <tr key={t.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 font-medium text-ink-900">{t.name ?? t.code}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{t.jurisdiction}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{t.rate_ppm !== null ? `${t.rate_ppm / 10000}%` : 'no active version'}</td>
                <td className="px-5 py-3 text-right">{editable ? <Button variant="danger" onClick={() => detachTax.mutate(t.id)}>Detach</Button> : null}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}
